import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ReactFlow, Background, Controls, useNodesState, useEdgesState, ReactFlowProvider, useReactFlow, MarkerType, reconnectEdge } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Box, CircularProgress } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import PromptNode from './nodes/PromptNode';
import TemplateNode from './nodes/TemplateNode';
import SubAgentContainerNode from './nodes/SubAgentContainerNode';
import SettingsNode from './nodes/SettingsNode';
import DataSchemaNode from './nodes/DataSchemaNode';
import GlobalToolsNode from './nodes/GlobalToolsNode';
import PromptSlotNode from './nodes/PromptSlotNode';
import { adminAgentsAPI, canvasVersionsAPI } from '../../../services/api';
import { toSnakeCase, FORMS_TEMPLATE_MARKER } from './promptUtils';
import ConditionEdge from './ConditionEdge';

const edgeTypes = { condition: ConditionEdge };

const nodeTypes = {
  prompt: PromptNode,
  execution_rules: TemplateNode,
  knowledge_management: TemplateNode,
  intelligence_guidelines: TemplateNode,
  forms: TemplateNode,
  sub_agent_container: SubAgentContainerNode,
  settings: SettingsNode,
  data_schema: DataSchemaNode,
  global_tools: GlobalToolsNode,
  prompt_slot: PromptSlotNode,
};

const TEMPLATE_NODE_LABELS = {
  execution_rules: 'Execution Rules',
  knowledge_management: 'Knowledge Mgmt',
  intelligence_guidelines: 'Guidelines',
  forms: 'Forms',
};

// Picks source/target handle pair for tool/sub-agent → prompt edges.
// Prefers handles not already occupied; uses relative position to determine best fit.
function getBestRefHandles(sourceNode, targetNode, occupiedTargetHandles) {
  const cx = (sourceNode.position?.x || 0) + (sourceNode.measured?.width || 160) / 2;
  const cy = (sourceNode.position?.y || 0) + (sourceNode.measured?.height || 80) / 2;
  const tx = (targetNode.position?.x || 0) + (targetNode.measured?.width || 180) / 2;
  const ty = (targetNode.position?.y || 0) + (targetNode.measured?.height || 80) / 2;
  const dx = tx - cx; // positive = prompt is to the right
  const dy = ty - cy; // positive = prompt is below
  const candidates = [
    { sourceHandle: 'source-right',  targetHandle: 'target-left',   score: dx  },
    { sourceHandle: 'source-bottom', targetHandle: 'target-top',    score: dy  },
    { sourceHandle: 'source-left',   targetHandle: 'target-right',  score: -dx },
    { sourceHandle: 'source-top',    targetHandle: 'target-bottom', score: -dy },
  ];
  candidates.sort((a, b) => {
    const aUsed = occupiedTargetHandles.has(a.targetHandle) ? 1 : 0;
    const bUsed = occupiedTargetHandles.has(b.targetHandle) ? 1 : 0;
    if (aUsed !== bUsed) return aUsed - bUsed;
    return b.score - a.score;
  });
  return { sourceHandle: candidates[0].sourceHandle, targetHandle: candidates[0].targetHandle };
}

// Parses "key: value" lines from the body of a $class.method(...) call.
function parseInlineBindings(paramsStr) {
  if (!paramsStr) return {};
  const bindings = {};
  for (const line of paramsStr.split('\n')) {
    const colonMatch = line.match(/^\s*(\w+)\s*:\s*(.+?)\s*,?\s*$/);
    if (colonMatch) {
      bindings[colonMatch[1]] = colonMatch[2].trim();
      continue;
    }
    // Bare positional args: "transaction_id" or "transaction_id, amount"
    for (const part of line.split(',')) {
      const bare = part.trim();
      if (/^\w+$/.test(bare)) bindings[bare] = '';
    }
  }
  return bindings;
}

function hasCycle(sourceId, targetId, currentEdges) {
  const adj = {};
  currentEdges.forEach((e) => {
    if (!adj[e.source]) adj[e.source] = [];
    adj[e.source].push(e.target);
  });
  const visited = new Set();
  const queue = [targetId];
  while (queue.length) {
    const curr = queue.shift();
    if (curr === sourceId) return true;
    if (visited.has(curr)) continue;
    visited.add(curr);
    (adj[curr] || []).forEach((n) => queue.push(n));
  }
  return false;
}

const draftKey = (agent) => `canvas_draft_${agent}`;

// Prompt-like types whose content can contain {{slot}} tokens.
const PROMPT_SLOT_PARENT_TYPES = new Set(['prompt', 'execution_rules', 'knowledge_management', 'intelligence_guidelines', 'forms']);

/** Extract unique slot token names from prompt content. */
function parseSlotTokens(content) {
  return [...new Set([...content.matchAll(/\{\{([a-zA-Z0-9_]+)\}\}/g)].map((m) => m[1]))];
}

// djb2 hash of the server canvas (nodes+edges). Stored on each draft so we can
// detect when the server canvas drifted out from under a stale local draft.
function hashCanvas(nodes, edges) {
  const str = JSON.stringify({ n: nodes || [], e: edges || [] });
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  return h.toString(36);
}

function NodeCanvasInner({ agentName, onSelectNode, onOpenCanvas, setNodesRef, addToolNodeRef, addSubAgentNodeRef, edgesRef, setEdgesRef, onNodesSnapshot, onEdgesSnapshot, onDraftChange, onStaleDraft, undoRef, discardDraftRef, reloadRef, onUndoStateChange }) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const { screenToFlowPosition } = useReactFlow();
  const saveTimer = useRef(null);
  // Tracks "promptId:agentName" keys for agents just added but not yet in prompt content
  const pendingSubAgents = useRef(new Set());

  const hasLoaded = useRef(false);
  // Snapshot of the canvas state at load time, plus after each successful save.
  // The save effect compares against this to skip writes when ReactFlow has
  // emitted change events that don't actually mutate persisted content
  // (selection, hover, layout reflow, etc.).
  const lastSavedSnapshot = useRef(null);
  const onOpenCanvasRef = useRef(onOpenCanvas);
  useEffect(() => { onOpenCanvasRef.current = onOpenCanvas; }, [onOpenCanvas]);
  const onNodesSnapshotRef = useRef(onNodesSnapshot);
  useEffect(() => { onNodesSnapshotRef.current = onNodesSnapshot; }, [onNodesSnapshot]);
  const onEdgesSnapshotRef = useRef(onEdgesSnapshot);
  useEffect(() => { onEdgesSnapshotRef.current = onEdgesSnapshot; }, [onEdgesSnapshot]);
  const onStaleDraftRef = useRef(onStaleDraft);
  useEffect(() => { onStaleDraftRef.current = onStaleDraft; }, [onStaleDraft]);
  // Hash of the server canvas at the time of load. The save effect stamps this
  // onto each draft so we can detect drift if the server canvas changes.
  const serverHashRef = useRef(null);
  // Session-only undo history. Ring buffer of serialised {nodes,edges} snapshots,
  // pushed on debounced change. Cleared on load.
  const historyRef = useRef([]);
  const HISTORY_LIMIT = 50;
  const historyTimer = useRef(null);
  const isApplyingUndo = useRef(false);
  const onUndoStateChangeRef = useRef(onUndoStateChange);
  useEffect(() => { onUndoStateChangeRef.current = onUndoStateChange; }, [onUndoStateChange]);
  // Bridge so discardDraft can trigger a fresh server load from outside the load effect.
  const triggerReload = useRef(null);

  useEffect(() => {
    if (!agentName) return;
    let cancelled = false;
    let loadId = 0;

    const load = (opts = {}) => {
      const { skipDraft = false } = opts;
      const myLoadId = ++loadId;
      hasLoaded.current = false;
      lastSavedSnapshot.current = null;
      historyRef.current = [];
      onUndoStateChangeRef.current?.(false);
      setLoading(true);
      setNodes([]);
      setEdges([]);
      Promise.all([
        adminAgentsAPI.getCanvas(agentName, { bust: skipDraft }).catch(() => ({ canvas_nodes: [], canvas_edges: [] })),
        canvasVersionsAPI.list(agentName).catch(() => ({ versions: [] })),
      ]).then(([canvasData, versionsData]) => {
      if (cancelled || loadId !== myLoadId) return;

      const serverHash = hashCanvas(canvasData.canvas_nodes, canvasData.canvas_edges);
      serverHashRef.current = serverHash;

      // Check for a localStorage draft saved during a previous unsaved session.
      // Only honour a draft if it actually has nodes — empty drafts are an
      // artefact of past loading bugs and would otherwise wipe the live canvas.
      let draft = null;
      try {
        const raw = localStorage.getItem(draftKey(agentName));
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed?.nodes?.length > 0) {
            draft = parsed;
          } else {
            localStorage.removeItem(draftKey(agentName));
          }
        }
      } catch {
        localStorage.removeItem(draftKey(agentName));
      }

      if (skipDraft && draft) {
        localStorage.removeItem(draftKey(agentName));
        draft = null;
      }

      // Drift detection: draft was built against an older server canvas (or has
      // no stamp from a pre-detection version). Surface to the user instead of
      // silently choosing one side.
      const draftStale = !!draft && draft.serverHash !== serverHash;
      if (draftStale) {
        onStaleDraftRef.current?.({
          discard: () => load({ skipDraft: true }),
          keep: () => {
            try {
              const raw = localStorage.getItem(draftKey(agentName));
              if (raw) {
                const parsed = JSON.parse(raw);
                parsed.serverHash = serverHashRef.current;
                localStorage.setItem(draftKey(agentName), JSON.stringify(parsed));
              }
            } catch { /* ignore */ }
          },
        });
      } else {
        onStaleDraftRef.current?.(null);
      }

      // Load order: localStorage draft (only if non-empty) > server canvas.
      const rawNodes = draft?.nodes || canvasData.canvas_nodes || [];
      const rawEdges = draft?.edges || canvasData.canvas_edges || [];
      onDraftChange?.(!!draft);

      if (!rawNodes?.length) {
        onDraftChange?.(false);
        const systemId = uuidv4();
        setNodes([
          { id: systemId, type: 'prompt', position: { x: 300, y: 200 }, data: { label: 'system', content: '', promptType: 'base' } },
          { id: 'execution_rules', type: 'execution_rules', position: { x: 300, y: 500 }, data: { label: 'Execution Rules', content: '' } },
          { id: 'settings', type: 'settings', position: { x: 700, y: 200 }, data: {} },
          { id: 'data_schema', type: 'data_schema', position: { x: 700, y: 380 }, data: {} },
        ]);
        setEdges([{
          id: `${systemId}-execution_rules`,
          source: systemId,
          target: 'execution_rules',
          sourceHandle: 'source-bottom',
          targetHandle: 'target-top',
          type: 'condition',
          data: { behavior: 'append' },
        }]);
        hasLoaded.current = true;
        setLoading(false);
        return;
      }

      const restored = rawNodes
        .map((n) => (n.type === 'sub_agent' || n.type === 'sub_agent_container')
          ? { ...n, data: { ...n.data, onOpenCanvas: onOpenCanvasRef.current } }
          : n
        )
        .filter((n) => n.type !== 'tool_class')
        .filter((n) => n.type !== 'tool_container')
        // Keep sub_agent_container nodes that either: (a) carry the legacy
        // promptNodeId link, or (b) have a populated subAgents[] (the
        // canvas-seeded format used by Phase 7.6 seeders).
        .filter((n) => n.type !== 'sub_agent_container' || n.data?.promptNodeId || (n.data?.subAgents?.length > 0));
      setNodes(restored);

      if (rawEdges?.length) {
        const promptNodeIds = new Set(restored.filter((n) => n.type === 'prompt').map((n) => n.id));
        const templateNodeIds = new Set(restored.filter((n) => TEMPLATE_NODE_LABELS[n.type]).map((n) => n.id));
        setEdges(rawEdges.map((e) => {
          const withType = e.type ? e : { ...e, type: 'condition' };
          if (!withType.sourceHandle && promptNodeIds.has(withType.source) && templateNodeIds.has(withType.target)) {
            return { ...withType, sourceHandle: 'source-bottom', targetHandle: 'target-top' };
          }
          return withType;
        }));
      }
      requestAnimationFrame(() => {
        if (!cancelled) {
          // Reset the baseline. The save effect will establish it on its first
          // post-load run (so it serializes the *same* shape it diffs against,
          // including any normalisation ReactFlow applies on mount).
          lastSavedSnapshot.current = null;
          hasLoaded.current = true;
          setLoading(false);
        }
      });
    });
    };

    triggerReload.current = () => load({ skipDraft: true });
    load();
    return () => { cancelled = true; };
  }, [agentName]);

  useEffect(() => {
    if (!agentName || !hasLoaded.current) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const serialisableNodes = nodes.map((n) => {
        // Strip transient/visual state injected by ReactFlow after render.
        const { onOpenCanvas: _onOpen, tools: _tools, isOrphaned: _iso, ...cleanData } = n.data || {};
        const { selected: _sel, dragging: _drag, measured: _m, width: _w, height: _h, positionAbsolute: _pa, ...cleanNode } = n;
        return { ...cleanNode, data: cleanData };
      });
      const cleanEdges = edges.map(({ selected: _sel, ...rest }) => rest);
      // comparableSnapshot intentionally excludes serverHash so that re-stamping
      // (via the "Keep my draft" action) doesn't look like a content change.
      const comparableSnapshot = JSON.stringify({ nodes: serialisableNodes, edges: cleanEdges });
      const snapshot = JSON.stringify({ nodes: serialisableNodes, edges: cleanEdges, serverHash: serverHashRef.current });

      // First run after load: establish the baseline using this exact
      // serialisation, but DO NOT write. Subsequent runs only write when the
      // serialised state genuinely differs (i.e. user actually changed
      // something — not selection/hover/layout reflow noise from ReactFlow).
      if (lastSavedSnapshot.current === null) {
        lastSavedSnapshot.current = comparableSnapshot;
        return;
      }
      if (comparableSnapshot === lastSavedSnapshot.current) return;

      try { localStorage.setItem(draftKey(agentName), snapshot); } catch { /* ignore */ }
      lastSavedSnapshot.current = comparableSnapshot;
      onDraftChange?.(true);
    }, 800);
    return () => clearTimeout(saveTimer.current);
  }, [nodes, edges, agentName]);

  // Push a snapshot to the undo history on debounced change. Skipped when an
  // undo is being applied (otherwise the restore itself would pollute history).
  useEffect(() => {
    if (!hasLoaded.current || isApplyingUndo.current) return;
    clearTimeout(historyTimer.current);
    historyTimer.current = setTimeout(() => {
      const snapshot = JSON.stringify({ nodes, edges });
      const last = historyRef.current[historyRef.current.length - 1];
      if (last === snapshot) return;
      historyRef.current.push(snapshot);
      if (historyRef.current.length > HISTORY_LIMIT) historyRef.current.shift();
      onUndoStateChangeRef.current?.(historyRef.current.length > 1);
    }, 500);
    return () => clearTimeout(historyTimer.current);
  }, [nodes, edges]);

  const undo = useCallback(() => {
    // Need at least 2 entries: the current state and the one to restore to.
    if (historyRef.current.length < 2) return;
    historyRef.current.pop();
    const prev = historyRef.current[historyRef.current.length - 1];
    try {
      const { nodes: pNodes, edges: pEdges } = JSON.parse(prev);
      isApplyingUndo.current = true;
      setNodes(pNodes);
      setEdges(pEdges);
      requestAnimationFrame(() => { isApplyingUndo.current = false; });
      onUndoStateChangeRef.current?.(historyRef.current.length > 1);
    } catch { /* ignore */ }
  }, [setNodes, setEdges]);

  const discardDraft = useCallback(() => {
    if (agentName) {
      try { localStorage.removeItem(draftKey(agentName)); } catch { /* ignore */ }
    }
    onStaleDraftRef.current?.(null);
    // Force a fresh server load via the effect-scoped helper. Bumping agentName
    // would change the dep; instead we use the effect's local closure by
    // simulating an unmount+remount of the loader via a state nudge.
    // Simpler: trigger via the load function exposed by a ref-style hook below.
    triggerReload.current?.();
  }, [agentName]);

  // Keep refs in sync with current implementations so parents can call them.
  useEffect(() => { if (undoRef) undoRef.current = undo; }, [undo, undoRef]);
  useEffect(() => { if (discardDraftRef) discardDraftRef.current = discardDraft; }, [discardDraft, discardDraftRef]);
  useEffect(() => { if (reloadRef) reloadRef.current = () => triggerReload.current?.(); }, [reloadRef]);

  // Cmd/Ctrl+Z hotkey — but only when the user is NOT in a text input or
  // CodeMirror editor (those have their own undo stacks that should win).
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.key === 'z' || e.key === 'Z') || !(e.metaKey || e.ctrlKey) || e.shiftKey) return;
      const el = document.activeElement;
      const tag = el?.tagName;
      const inEditable = tag === 'INPUT' || tag === 'TEXTAREA' || el?.isContentEditable || el?.closest?.('.cm-editor');
      if (inEditable) return;
      e.preventDefault();
      undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo]);

  const PROMPT_LIKE_TYPES_SET = new Set(['prompt', 'execution_rules', 'knowledge_management', 'intelligence_guidelines']);

  // Syncs tool refs from prompt content directly into the prompt node's data.tools.
  // No separate canvas node — tools are shown in the properties panel below the editor.
  useEffect(() => {
    const promptNodes = nodes.filter((n) => PROMPT_LIKE_TYPES_SET.has(n.type));
    const nodeUpdates = new Map();

    for (const promptNode of promptNodes) {
      const content = promptNode.data?.content || '';
      // Dedupe by (classSnake, methodName) — a prompt may reference the same
      // tool many times (e.g. $system_tools.hand_over_to_agent appears in step
      // bodies, rules, etc.). First occurrence wins, so any inline bindings on
      // the first call site are the ones kept.
      const seenRefKeys = new Set();
      const refs = [];
      for (const m of content.matchAll(/\$(\w+)\.(\w+)(?:\(([^)]*)\))?/gs)) {
        const [, classSnake, methodName, paramsStr] = m;
        const k = `${classSnake}.${methodName}`;
        if (seenRefKeys.has(k)) continue;
        seenRefKeys.add(k);
        refs.push({ classSnake, methodName, inlineBindings: parseInlineBindings(paramsStr) });
      }

      const existingTools = promptNode.data?.tools || [];
      const newTools = refs.map(({ classSnake, methodName, inlineBindings }) => {
        const found = existingTools.find((t) => toSnakeCase(t.toolClass) === classSnake && t.methodName === methodName);
        const storedBindings = found?.parameterBindings || {};
        const parameterBindings = Object.keys(inlineBindings).length > 0 ? inlineBindings : storedBindings;
        return found ? { ...found, parameterBindings } : { toolClass: classSnake, methodName, parameterBindings };
      });

      const existingKey = existingTools.map((t) => `${toSnakeCase(t.toolClass)}.${t.methodName}`).sort().join(',');
      const newKey = newTools.map((t) => `${toSnakeCase(t.toolClass)}.${t.methodName}`).sort().join(',');
      const bindingsChanged = newTools.some((nt) => {
        const et = existingTools.find((t) => toSnakeCase(t.toolClass) === nt.toolClass && t.methodName === nt.methodName);
        return JSON.stringify(nt.parameterBindings) !== JSON.stringify(et?.parameterBindings || {});
      });

      if (existingKey !== newKey || bindingsChanged) {
        nodeUpdates.set(promptNode.id, { ...promptNode.data, tools: newTools });
      }
    }

    if (nodeUpdates.size > 0) {
      setNodes((prev) => prev.map((n) => nodeUpdates.has(n.id) ? { ...n, data: nodeUpdates.get(n.id) } : n));
    }
  }, [nodes]);

  // Syncs one sub_agent_container per prompt. Container ID: sub-agent-container-{promptId}.
  // Reads @AgentName refs from content; pendingSubAgents protects against the one-cycle lag
  // between handleAddSubAgentNode updating the container and the content update propagating.
  useEffect(() => {
    const subAgentContainerNodes = nodes.filter((n) => n.type === 'sub_agent_container');
    const promptNodes = nodes.filter((n) => PROMPT_LIKE_TYPES_SET.has(n.type));

    const subAgentRefEdges = [];
    const activeContainerIds = new Set();
    const nodeUpdates = new Map();
    const newNodes = [];

    for (const promptNode of promptNodes) {
      const content = promptNode.data?.content || '';
      const contentAgentNames = new Set([...content.matchAll(/(?<!\w)@(\w+)/g)].map(([, name]) => name));

      // Include agents that were just added but haven't propagated to content yet
      const pendingForPrompt = new Set(
        [...pendingSubAgents.current]
          .filter((k) => k.startsWith(`${promptNode.id}:`))
          .map((k) => k.split(':')[1])
      );
      const allAgentNames = new Set([...contentAgentNames, ...pendingForPrompt]);

      if (allAgentNames.size === 0) continue;

      const containerId = `sub-agent-container-${promptNode.id}`;
      activeContainerIds.add(containerId);
      const existingContainer = subAgentContainerNodes.find((n) => n.id === containerId);

      const newSubAgents = [...allAgentNames].map((agentName) => {
        const found = existingContainer?.data.subAgents?.find((s) => s.agentName === agentName);
        return found || { agentName, inputBindings: {} };
      });

      // Clear pending for this prompt now that we've included them in newSubAgents
      for (const key of [...pendingSubAgents.current]) {
        if (key.startsWith(`${promptNode.id}:`)) pendingSubAgents.current.delete(key);
      }

      if (existingContainer) {
        const existingKey = (existingContainer.data.subAgents || []).map((s) => s.agentName).sort().join(',');
        const newKey = newSubAgents.map((s) => s.agentName).sort().join(',');
        const needsUpdate = existingKey !== newKey || existingContainer.data.onOpenCanvas !== onOpenCanvasRef.current;
        if (needsUpdate) {
          nodeUpdates.set(containerId, { ...existingContainer.data, subAgents: newSubAgents, onOpenCanvas: onOpenCanvasRef.current });
        }

        const existingSubAgentEdge = (edgesRef?.current || []).find((e) => e.id === `sub-agent-ref-${promptNode.id}`);
        let sh, th;
        if (existingSubAgentEdge) {
          sh = existingSubAgentEdge.sourceHandle;
          th = existingSubAgentEdge.targetHandle;
        } else {
          const occupiedTarget = new Set(
            (edgesRef?.current || [])
              .filter((e) => e.data?.toolRef && e.target === promptNode.id)
              .map((e) => e.targetHandle)
          );
          ({ sourceHandle: sh, targetHandle: th } = getBestRefHandles(existingContainer, promptNode, occupiedTarget));
        }
        subAgentRefEdges.push({
          id: `sub-agent-ref-${promptNode.id}`,
          source: containerId,
          sourceHandle: sh,
          target: promptNode.id,
          targetHandle: th,
          data: { subAgentRef: true },
          style: { stroke: '#a855f7', strokeDasharray: '5 3' },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#a855f7' },
        });
      } else {
        newNodes.push({
          id: containerId,
          type: 'sub_agent_container',
          position: { x: (promptNode.position?.x || 0) - 220, y: (promptNode.position?.y || 0) + 120 },
          data: { promptNodeId: promptNode.id, subAgents: newSubAgents, onOpenCanvas: onOpenCanvasRef.current },
        });
      }
    }

    const orphanedIds = subAgentContainerNodes
      .filter((n) => !activeContainerIds.has(n.id))
      .map((n) => n.id);

    setEdges((prev) => [...prev.filter((e) => !e.data?.subAgentRef), ...subAgentRefEdges]);

    if (orphanedIds.length > 0 || nodeUpdates.size > 0 || newNodes.length > 0) {
      setNodes((prev) => [
        ...prev
          .filter((n) => !orphanedIds.includes(n.id))
          .map((n) => nodeUpdates.has(n.id) ? { ...n, data: nodeUpdates.get(n.id) } : n),
        ...newNodes,
      ]);
    }
  }, [nodes]);

  // Syncs prompt_slot nodes and slot edges from {{token}} references in prompt content.
  // Follows the same pattern as the tool-sync and sub-agent-sync effects above.
  useEffect(() => {
    const promptNodes = nodes.filter((n) => PROMPT_SLOT_PARENT_TYPES.has(n.type));

    // Collect desired slot edges across all prompt nodes, and any new slot nodes needed.
    const desiredSlotEdges = [];
    const newSlotNodes = [];
    // Tracks orphans claimed in this pass so two prompt nodes can't both claim the same one.
    const claimedOrphanIds = new Set();

    for (const promptNode of promptNodes) {
      const content = promptNode.data?.content || '';
      const tokens = parseSlotTokens(content);
      if (tokens.length === 0) continue;

      const parentPos = promptNode.position || { x: 300, y: 200 };

      for (const name of tokens) {
        // Prefer an existing slot node with this name that has no incoming slot edge (orphaned).
        const orphaned = nodes.find(
          (n) =>
            n.type === 'prompt_slot' &&
            n.data?.name === name &&
            !edges.some((e) => e.target === n.id && e.data?.edgeKind === 'slot') &&
            !claimedOrphanIds.has(n.id)
        );
        // Also accept a slot node already connected to this specific prompt.
        const alreadyConnected = nodes.find(
          (n) =>
            n.type === 'prompt_slot' &&
            n.data?.name === name &&
            edges.some((e) => e.source === promptNode.id && e.target === n.id && e.data?.edgeKind === 'slot')
        );

        let targetId;
        if (alreadyConnected) {
          targetId = alreadyConnected.id;
        } else if (orphaned) {
          targetId = orphaned.id;
          claimedOrphanIds.add(orphaned.id);
        } else {
          // Create a new slot node positioned to the right of the prompt.
          const slotCount = nodes.filter((n) => n.type === 'prompt_slot').length + newSlotNodes.length;
          const newNode = {
            id: `slot_${name}_${uuidv4().slice(0, 8)}`,
            type: 'prompt_slot',
            position: { x: parentPos.x + 340, y: parentPos.y + slotCount * 190 },
            data: { name, variants: [], fallback: '' },
          };
          newSlotNodes.push(newNode);
          targetId = newNode.id;
        }

        desiredSlotEdges.push({
          id: `slot_${promptNode.id}_${name}`,
          source: promptNode.id,
          target: targetId,
          sourceHandle: 'source-right',
          targetHandle: 'target-left',
          type: 'condition',
          data: { edgeKind: 'slot', slotName: name },
        });
      }
    }

    // Determine which slot edges need to be added or removed.
    const desiredIds = new Set(desiredSlotEdges.map((e) => e.id));
    const existingSlotEdges = edges.filter((e) => e.data?.edgeKind === 'slot');
    const existingIds = new Set(existingSlotEdges.map((e) => e.id));

    const existingById = Object.fromEntries(existingSlotEdges.map((e) => [e.id, e]));
    const edgesChanged =
      desiredSlotEdges.some((e) => !existingIds.has(e.id)) ||
      existingSlotEdges.some((e) => !desiredIds.has(e.id)) ||
      desiredSlotEdges.some((e) => existingById[e.id] && existingById[e.id].target !== e.target);

    if (newSlotNodes.length === 0 && !edgesChanged) return;

    if (newSlotNodes.length > 0) {
      setNodes((prev) => [...prev, ...newSlotNodes]);
    }

    if (edgesChanged) {
      setEdges((prev) => [
        ...prev.filter((e) => e.data?.edgeKind !== 'slot'),
        ...desiredSlotEdges,
      ]);
    }
  }, [nodes]);

  // Stamps isOrphaned on prompt_slot nodes whenever edges change.
  // Guard: return same ns reference if nothing changed to avoid infinite loop.
  useEffect(() => {
    const slotEdgeTargets = new Set(
      edges.filter((e) => e.data?.edgeKind === 'slot').map((e) => e.target)
    );
    setNodes((ns) => {
      if (!ns.some((n) => n.type === 'prompt_slot')) return ns;
      let changed = false;
      const next = ns.map((n) => {
        if (n.type !== 'prompt_slot') return n;
        const shouldBeOrphaned = !slotEdgeTargets.has(n.id);
        if (n.data?.isOrphaned === shouldBeOrphaned) return n;
        changed = true;
        return { ...n, data: { ...n.data, isOrphaned: shouldBeOrphaned } };
      });
      return changed ? next : ns;
    });
  }, [edges]);

  useLayoutEffect(() => {
    if (setNodesRef) setNodesRef.current = setNodes;
  }, [setNodes, setNodesRef]);

  useLayoutEffect(() => {
    if (onNodesSnapshotRef.current) onNodesSnapshotRef.current(nodes);
  }, [nodes]);

  useLayoutEffect(() => {
    if (setEdgesRef) setEdgesRef.current = setEdges;
  }, [setEdges, setEdgesRef]);

  useLayoutEffect(() => {
    if (edgesRef) edgesRef.current = edges;
    if (onEdgesSnapshotRef.current) onEdgesSnapshotRef.current(edges);
  }, [edges, edgesRef]);

  const onConnect = useCallback(
    (params) => {
      const sourceNode = nodes.find((n) => n.id === params.source);
      const targetNode = nodes.find((n) => n.id === params.target);
      const CONNECTABLE = new Set(['prompt', ...Object.keys(TEMPLATE_NODE_LABELS)]);
      if (!CONNECTABLE.has(sourceNode?.type) || !CONNECTABLE.has(targetNode?.type)) return;
      if (hasCycle(params.source, params.target, edges)) return;
      setEdges((prev) => [
        ...prev,
        { id: uuidv4(), type: 'condition', source: params.source, target: params.target, sourceHandle: params.sourceHandle, targetHandle: params.targetHandle, data: { behavior: 'append' } },
      ]);
    },
    [nodes, edges, setEdges],
  );

  const onReconnect = useCallback((oldEdge, newConnection) => {
    setEdges((els) => reconnectEdge(oldEdge, newConnection, els));
  }, [setEdges]);

  // No-op: tool text is inserted by PromptEditor; the sync useEffect above picks it up automatically.
  const handleAddToolNode = useCallback(() => {}, []);

  useLayoutEffect(() => {
    if (addToolNodeRef) addToolNodeRef.current = handleAddToolNode;
  }, [handleAddToolNode, addToolNodeRef]);

  // Creates or updates the sub_agent_container for the given prompt node.
  const handleAddSubAgentNode = useCallback((subAgent, promptNodeId) => {
    if (!promptNodeId) return;
    pendingSubAgents.current.add(`${promptNodeId}:${subAgent.agentName}`);
    setNodes((prev) => {
      const containerId = `sub-agent-container-${promptNodeId}`;
      const existing = prev.find((n) => n.id === containerId);
      if (existing) {
        if ((existing.data.subAgents || []).some((s) => s.agentName === subAgent.agentName)) return prev;
        return prev.map((n) => n.id === containerId
          ? { ...n, data: { ...n.data, subAgents: [...(n.data.subAgents || []), { agentName: subAgent.agentName, inputBindings: {}, versionPin: 'latest' }] } }
          : n
        );
      }
      const promptNode = prev.find((n) => n.id === promptNodeId);
      const pos = promptNode
        ? { x: promptNode.position.x - 220, y: promptNode.position.y + 120 }
        : { x: 100, y: 250 };
      return [...prev, {
        id: containerId,
        type: 'sub_agent_container',
        position: pos,
        data: { promptNodeId, subAgents: [{ agentName: subAgent.agentName, inputBindings: {}, versionPin: 'latest' }], onOpenCanvas: onOpenCanvasRef.current },
      }];
    });
  }, [setNodes]);

  useLayoutEffect(() => {
    if (addSubAgentNodeRef) addSubAgentNodeRef.current = handleAddSubAgentNode;
  }, [handleAddSubAgentNode, addSubAgentNodeRef]);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      const nodeType = e.dataTransfer.getData('nodeType');
      if (!nodeType) return;
      const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });

      if (TEMPLATE_NODE_LABELS[nodeType]) {
        // Forms nodes track the central template via the {{FORMS}} marker (resolved at
        // prompt assembly) instead of freezing a copy of forms.txt into the canvas.
        const seedContent = nodeType === 'forms' ? FORMS_TEMPLATE_MARKER : '';
        const newNode = {
          id: uuidv4(),
          type: nodeType,
          position,
          data: { label: TEMPLATE_NODE_LABELS[nodeType], content: seedContent, matchMode: 'union' },
        };
        setNodes((prev) => [...prev, newNode]);
        if (nodeType !== 'forms') {
          adminAgentsAPI.getPromptTemplate(nodeType).then(({ content }) => {
            setNodes((prev) =>
              prev.map((n) => (n.id === newNode.id ? { ...n, data: { ...n.data, content } } : n))
            );
          }).catch((err) => console.error('Failed to load prompt template for', nodeType, err));
        }
        return;
      }

      const newNode = {
        id: uuidv4(),
        type: nodeType,
        position,
        data: nodeType === 'prompt'
          ? { label: 'Prompt', content: '' }
          : {},
      };
      setNodes((prev) => [...prev, newNode]);
    },
    [screenToFlowPosition, setNodes],
  );

  return (
    <Box sx={{ flex: 1, height: '100%', position: 'relative' }} onDragOver={onDragOver} onDrop={onDrop}>
      {loading && (
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
          <CircularProgress size={32} />
        </Box>
      )}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onReconnect={onReconnect}
        onNodeClick={(_, node) => onSelectNode(node)}
        onEdgeClick={(_, edge) => onSelectNode(edge)}
        onPaneClick={() => onSelectNode(null)}
        connectionMode="loose"
        fitView
      >
        <Background variant="dots" gap={24} size={1} color="#333" />
        <Controls />
      </ReactFlow>
    </Box>
  );
}

const NodeCanvas = React.memo(function NodeCanvas({ setNodesRef, addToolNodeRef, addSubAgentNodeRef, edgesRef, setEdgesRef, onNodesSnapshot, onEdgesSnapshot, ...props }) {
  return (
    <ReactFlowProvider>
      <NodeCanvasInner
        setNodesRef={setNodesRef}
        addToolNodeRef={addToolNodeRef}
        addSubAgentNodeRef={addSubAgentNodeRef}
        edgesRef={edgesRef}
        setEdgesRef={setEdgesRef}
        onNodesSnapshot={onNodesSnapshot}
        onEdgesSnapshot={onEdgesSnapshot}
        {...props}
      />
    </ReactFlowProvider>
  );
});

export default NodeCanvas;
